/**
 * Taking a discount off a bill, WITHOUT pricing it (moved out of `./index` on 2026-10-06): the
 * partner release, the local strip and the coupon answer's reset. `removeDiscount` (`./index`)
 * prices and commits after it; the shared write path (`ordering/writePath`) uses it to sweep a
 * coupon a repricing dropped, as web's `removeUnappliedCoupons` did through `removeDiscountFromCart`.
 * Nothing here imports the pricing, so the write path and this module do not import each other.
 */
import store from "../redux-store";
import { applyDiscountSuccess } from "../redux-store/slices/DiscountSlice";
import { unBlockExternalDiscount } from "../redux-store/slices/Loyalty";
import { notifyError } from "../effects-bridge";
import { copy } from "../copy";
import { isLoyaltyPointDiscountType } from "../loyalty/pointsDiscount";
import { planCouponUnblock } from "./external";
import {
  isExternalCouponProvider,
  requiresCouponUnblock,
  resolveDiscountProvider,
} from "./provider";
import { stripBillDiscount } from "./billDiscount";
import {
  selectActiveCouponProvider,
  selectAppliedDiscountInfo,
  selectExternalReferenceId,
  selectProviderByMetaId,
  selectSaleData,
} from "./selectors";
import { CUSTOMER_PHONE_MISSING_KEY, OFFER_FAILED_KEY } from "./constants";
import type { BillDiscount, Sale } from "./types";

/**
 * Is this entry bound to the CUSTOMER at a partner, whatever provider we resolved for it?
 *
 * 🔴 **A second, independent reason to send the release** — `requiresCouponUnblock` answers "does
 * this PARTNER block coupons", and this answers "is this DISCOUNT one a partner is holding
 * against the customer". Either is enough: a release that is never sent leaves the coupon blocked
 * at the partner forever, which is strictly worse than one sent needlessly.
 *
 * Ported from web's `removeDiscountFromCart` (DM-146, 2026-09-11), where the widening reads
 * `!!discount?.isExternal || isLoyaltyPointDiscountType(discount) || provider === DOTPE`. That
 * third disjunct is NOT repeated here and is not missing: DotPe's profile carries
 * `isExternalLoyalty: true`, so `isExternalCouponProvider` already answers for it — and on web it
 * is redundant too, since DotPe joined `EXTERNAL_LOYALTY_PROVIDER_METAIDS` in that same change.
 * What the widening actually adds is the two DISCOUNT-shaped tests below.
 *
 * Its loyalty-points test is the loyalty core's pure one (`loyalty/pointsDiscount`) since
 * 2026-10-06; it was `helpers/loyaltyHelper`'s, which reaches the store at module-eval.
 *
 * ⚠️ `isLoyaltyPointDiscountType` is included for PARITY WITH WEB, not because the kiosk can
 * reach it: `hooks/useKioskDiscounts` filters redeemed points out of the applied-offer lookup on
 * purpose, so its Remove never sees one (stripping points as a discount would bypass the refund —
 * `docs/flows/LOYALTY_REFUND.md`). It is kept so any OTHER caller of `removeDiscount` gets web's
 * behaviour rather than a silently different one.
 */
function isCustomerBoundDiscount(discount: BillDiscount | undefined): boolean {
  return !!(discount as any)?.isExternal || !!isLoyaltyPointDiscountType(discount);
}

/**
 * Release a partner coupon that is coming off the bill.
 *
 * 🔴 **A no-op for a partner that blocks nothing**, and a REPORTED no-op for a partner that
 * requires fields we do not have — `planCouponUnblock` decides which, from the profile. Sending
 * an incomplete request produces a rejection the cashier cannot act on.
 *
 * ⚠️ The partner is resolved from the DISCOUNT, not from the active provider, for the reason
 * `resolveDiscountProvider` gives: a release sent to the wrong partner leaves the customer's
 * coupon blocked forever.
 */
export function releaseExternalCoupon(discount: BillDiscount, sale?: Sale): void {
  const state = store.getState();
  const activeProvider = selectActiveCouponProvider(state);
  const metaId = resolveDiscountProvider(discount as any, activeProvider?.metaId);
  if (!requiresCouponUnblock(metaId) && !isCustomerBoundDiscount(discount)) return;

  const providerId =
    selectProviderByMetaId(state, (discount as any)?.couponProvider)?.mid || activeProvider?.mid;
  const appliedDiscountInfo = selectAppliedDiscountInfo(state);
  const targetSale = sale ?? selectSaleData(state);

  const plan = planCouponUnblock({
    referenceId: selectExternalReferenceId(state),
    providerId,
    metaId,
    couponCode:
      appliedDiscountInfo?.couponCode ||
      (discount as any)?.couponCode ||
      (discount as any)?.discountCode,
    customerPhone: (targetSale as any)?.customerInfo?.contactInfo?.phoneNumber || "",
    sale: targetSale,
  });

  if (!plan.ok) {
    notifyError(
      copy(plan.missing === "customerPhone" ? CUSTOMER_PHONE_MISSING_KEY : OFFER_FAILED_KEY),
    );
    return;
  }

  store.dispatch(
    unBlockExternalDiscount({
      postBody: plan.body,
      // Web's `removeExternalDiscount` sends this beside the body, and the loyalty saga needs
      // BOTH it and `postBody.metaId` before it will refresh the customer after the release
      // (`saga/loyalty/shared.ts`). Omitting it silently disabled that refresh on this path.
      customerRefId: (targetSale as any)?.customerInfo?.customerRefId,
      // The release is fire-and-forget by design: the discount is already off the local bill,
      // and a partner that fails to release has its own reconciliation. The app logged here.
      callback: () => undefined,
    }),
  );
}

/**
 * Should the coupon API's cached answer be dropped when this discount comes off?
 *
 * True for anything that CAME from that API — a voucher, a coupon code, or a partner coupon that
 * needs unblocking. Carried over from `removeDiscountFromCart`, where the three tests were
 * spelled inline.
 */
function shouldClearCouponInfo(discount: BillDiscount, metaId?: string): boolean {
  return !!(
    (discount as any)?.discountType === "Voucher" ||
    (discount as any)?.couponCode ||
    requiresCouponUnblock(metaId)
  );
}

/**
 * Take one discount off a bill, unpriced: release, then strip, then the coupon answer.
 *
 * 🔴 **Two halves, and both belong here.** The local half strips the entry (or suppresses it, for
 * an auto discount); the partner half releases the coupon where the profile says the partner
 * blocked it. A caller that does only the first leaves the customer's coupon blocked at the
 * partner with nothing on the bill to release it from — and the coupon is then unusable on their
 * next visit.
 *
 * ⚠️ **The order is deliberate: RELEASE FIRST, then strip.** The release reads the coupon's
 * `couponProvider` and code off the discount and the phone number off the sale, so it has to run
 * while the discount is still on the bill it was applied to.
 *
 * ⚠️ Clearing `discountInfo` matches the app's `removeDiscountFromCart`: a voucher, a
 * class-1 partner coupon, or anything carrying a `couponCode` came from the coupon API, and
 * leaving its answer in the slice re-applies it on the next render (the surface watches that
 * slot).
 */
export function dropDiscount(discount: BillDiscount, sale: Sale): Sale {
  const metaId = resolveDiscountProvider(
    discount as any,
    selectActiveCouponProvider(store.getState())?.metaId,
  );
  if (isExternalCouponProvider(metaId) || isCustomerBoundDiscount(discount))
    releaseExternalCoupon(discount, sale);
  const stripped = stripBillDiscount(sale, discount);
  if (shouldClearCouponInfo(discount, metaId)) store.dispatch(applyDiscountSuccess({}));
  return stripped;
}
